import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, ChevronLeft, ChevronRight, ChevronUp, ChevronsUpDown,
  Loader2, AlertCircle, X, CheckSquare, Square, SlidersHorizontal,
} from 'lucide-react';

/**
 * Shape shared by HubSpotContact and SalesforceContact, so this table works for either CRM.
 */
export type SelectableContact = {
  id: string;
  properties: Record<string, string | null>;
};

type SortDirection = 'asc' | 'desc';

/** Why a contact cannot be imported, or null when it can. */
type ContactBlocker = 'no-email' | 'already-imported' | null;

const PAGE_SIZES = [25, 50, 100];

const readProp = (contact: SelectableContact, key: string): string =>
  (contact.properties[key] || '').trim();

const fullName = (contact: SelectableContact): string =>
  `${readProp(contact, 'firstname')} ${readProp(contact, 'lastname')}`.trim();

const COLUMNS: { key: string; labelKey: string; value: (c: SelectableContact) => string }[] = [
  { key: 'name', labelKey: 'crmImport.contactTable.columns.name', value: fullName },
  { key: 'email', labelKey: 'crmImport.contactTable.columns.email', value: (c) => readProp(c, 'email') },
  { key: 'company', labelKey: 'crmImport.contactTable.columns.company', value: (c) => readProp(c, 'company') },
  { key: 'jobtitle', labelKey: 'crmImport.contactTable.columns.jobTitle', value: (c) => readProp(c, 'jobtitle') },
  { key: 'lifecyclestage', labelKey: 'crmImport.contactTable.columns.lifecycleStage', value: (c) => readProp(c, 'lifecyclestage') },
  { key: 'phone', labelKey: 'crmImport.contactTable.columns.phone', value: (c) => readProp(c, 'phone') },
];

const SEARCHABLE_COLUMNS = ['name', 'email', 'company', 'jobtitle'];

/** Kept even when empty — these identify the row. */
const ALWAYS_VISIBLE_COLUMNS = ['name', 'email'];

const CONTROL_CLASS =
  'h-10 px-3 rounded-lg border border-gray-300 bg-white text-sm text-gray-800 cursor-pointer ' +
  'hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4339A0] focus:border-transparent ' +
  'transition-[border-color,box-shadow] duration-150';

interface ContactSelectionTableProps {
  contacts: SelectableContact[];
  selectedIds: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  /** Lower-cased emails already staged or already promoters, so they can be flagged and blocked. */
  existingEmails: Set<string>;
  isLoading: boolean;
  truncated: boolean;
  /** CRM name shown in the copy, e.g. "HubSpot" or "Salesforce". */
  sourceLabel: string;
}

const ContactSelectionTable: React.FC<ContactSelectionTableProps> = ({
  contacts,
  selectedIds,
  onSelectionChange,
  existingEmails,
  isLoading,
  truncated,
  sourceLabel,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [hideBlocked, setHideBlocked] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const blockerFor = (contact: SelectableContact): ContactBlocker => {
    const email = readProp(contact, 'email').toLowerCase();
    if (!email) return 'no-email';
    if (existingEmails.has(email)) return 'already-imported';
    return null;
  };

  const stages = Array.from(
    new Set(contacts.map((c) => readProp(c, 'lifecyclestage')).filter(Boolean))
  ).sort();

  // Drop columns no record has a value for, so Salesforce (which has no lifecycle stage) does
  // not render a column of dashes. Name and email always stay — they identify the row.
  const visibleColumns = COLUMNS.filter(
    (column) => ALWAYS_VISIBLE_COLUMNS.includes(column.key) || contacts.some((c) => column.value(c))
  );

  const needle = search.trim().toLowerCase();
  const filtered = contacts.filter((contact) => {
    if (stageFilter && readProp(contact, 'lifecyclestage') !== stageFilter) return false;
    if (hideBlocked && blockerFor(contact) !== null) return false;
    if (!needle) return true;
    // Substring match across the text columns - this is why the full set is loaded up front:
    // HubSpot's own search is token-based and would not match "ohn" against "John".
    return SEARCHABLE_COLUMNS.some((key) => {
      const column = COLUMNS.find((c) => c.key === key);
      return column ? column.value(contact).toLowerCase().includes(needle) : false;
    });
  });

  const sortColumn = COLUMNS.find((c) => c.key === sortKey) || COLUMNS[0];
  const sorted = [...filtered].sort((a, b) => {
    const result = sortColumn.value(a).localeCompare(sortColumn.value(b), undefined, { sensitivity: 'base' });
    return sortDirection === 'asc' ? result : -result;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  // Clamped rather than reset via an effect: filters can shrink the result set below the
  // current page at any time, and every handler that changes the set already resets to page 1.
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const selectableOnPage = pageRows.filter((c) => blockerFor(c) === null);
  const allOnPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((c) => selectedIds.has(c.id));
  const selectableInFilter = sorted.filter((c) => blockerFor(c) === null);
  const hasActiveFilters = Boolean(needle || stageFilter || hideBlocked);

  const resetToFirstPage = () => setPage(1);

  const resetFilters = () => {
    setSearch('');
    setStageFilter('');
    setHideBlocked(false);
    resetToFirstPage();
  };

  const toggleContact = (contact: SelectableContact) => {
    if (blockerFor(contact) !== null) return;
    const next = new Set(selectedIds);
    if (next.has(contact.id)) {
      next.delete(contact.id);
    } else {
      next.add(contact.id);
    }
    onSelectionChange(next);
  };

  const togglePage = () => {
    const next = new Set(selectedIds);
    if (allOnPageSelected) {
      selectableOnPage.forEach((c) => next.delete(c.id));
    } else {
      selectableOnPage.forEach((c) => next.add(c.id));
    }
    onSelectionChange(next);
  };

  const selectAllMatching = () => {
    const next = new Set(selectedIds);
    selectableInFilter.forEach((c) => next.add(c.id));
    onSelectionChange(next);
  };

  const toggleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortKey(key);
    setSortDirection('asc');
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.04)] p-16 text-center">
        <Loader2 className="w-10 h-10 text-[#4339A0] animate-spin mx-auto mb-4" />
        <p className="font-medium text-gray-900 text-balance">{t('crmImport.contactTable.loading', { source: sourceLabel })}</p>
        <p className="text-sm text-gray-600 mt-1 text-pretty">{t('crmImport.contactTable.loadingHint')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {truncated && (
        <div className="bg-orange-50 rounded-xl p-4 flex items-start gap-3 shadow-[inset_0_0_0_1px_rgba(234,88,12,0.18)]">
          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-orange-900 text-pretty">
            {t('crmImport.contactTable.truncated', { count: contacts.length, source: sourceLabel })}
          </p>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-xl p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.04)]">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                resetToFirstPage();
              }}
              placeholder={t('crmImport.contactTable.searchPlaceholder')}
              className="w-full h-10 pl-9 pr-10 rounded-lg border border-gray-300 text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4339A0] focus:border-transparent transition-[border-color,box-shadow] duration-150"
            />
            {search && (
              <button
                onClick={() => {
                  setSearch('');
                  resetToFirstPage();
                }}
                className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-md hover:bg-gray-100 active:scale-[0.96] transition-[background-color,transform] duration-150"
                aria-label={t('crmImport.contactTable.clearSearch')}
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>

          {/* Filter affordance: labelling the controls makes it obvious the list can be narrowed */}
          <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
            <SlidersHorizontal className="w-4 h-4" />
            <span>{t('crmImport.contactTable.filtersLabel')}</span>
          </div>

          {stages.length > 0 && (
            <select
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value);
                resetToFirstPage();
              }}
              className={`${CONTROL_CLASS} ${stageFilter ? 'border-[#4339A0] text-[#4339A0] font-medium' : ''}`}
            >
              <option value="">{t('crmImport.contactTable.allStages')}</option>
              {stages.map((stage) => (
                <option key={stage} value={stage}>{stage}</option>
              ))}
            </select>
          )}

          <label
            className={`h-10 px-3 inline-flex items-center gap-2 rounded-lg border text-sm cursor-pointer select-none transition-[border-color,background-color] duration-150 ${
              hideBlocked
                ? 'border-[#4339A0] bg-[#f5f3ff] text-[#4339A0] font-medium'
                : 'border-gray-300 text-gray-700 hover:border-gray-400'
            }`}
          >
            <input
              type="checkbox"
              checked={hideBlocked}
              onChange={(e) => {
                setHideBlocked(e.target.checked);
                resetToFirstPage();
              }}
              className="rounded border-gray-300 text-[#4339A0] focus:ring-[#4339A0] cursor-pointer"
            />
            <span>{t('crmImport.contactTable.hideUnavailable')}</span>
          </label>

          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              resetToFirstPage();
            }}
            className={`${CONTROL_CLASS} tabular-nums`}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{t('crmImport.contactTable.perPage', { count: size })}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="h-10 px-3 rounded-lg text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 active:scale-[0.96] transition-[background-color,color,transform] duration-150"
            >
              {t('crmImport.contactTable.resetFilters')}
            </button>
          )}
        </div>

        <div className="mt-3 pt-3 flex flex-wrap items-center justify-between gap-2 text-sm shadow-[inset_0_1px_0_rgba(0,0,0,0.06)]">
          <span className="text-gray-600 tabular-nums">
            {t('crmImport.contactTable.showing', { shown: sorted.length, total: contacts.length })}
          </span>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-[#4339A0] tabular-nums">
              {t('crmImport.contactTable.selectedCount', { count: selectedIds.size })}
            </span>
            {selectableInFilter.length > selectableOnPage.length && (
              <button
                onClick={selectAllMatching}
                className="h-10 px-2 -mx-2 text-[#4339A0] hover:underline active:scale-[0.96] transition-transform duration-150 tabular-nums"
              >
                {t('crmImport.contactTable.selectAllMatching', { count: selectableInFilter.length })}
              </button>
            )}
            {selectedIds.size > 0 && (
              <button
                onClick={() => onSelectionChange(new Set())}
                className="h-10 px-2 -mx-2 text-gray-600 hover:underline active:scale-[0.96] transition-transform duration-150"
              >
                {t('crmImport.contactTable.clearSelection')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.04)]">
        {/* Bounded height gives the sticky header something to stick to, and keeps the
            pagination and Continue button reachable on a 100-row page. */}
        <div className="overflow-auto max-h-[65vh]">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
              <tr>
                <th className="px-4 py-2 w-12">
                  <button
                    onClick={togglePage}
                    disabled={selectableOnPage.length === 0}
                    className="w-10 h-10 grid place-items-center rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent active:scale-[0.96] transition-[background-color,transform] duration-150"
                    aria-label={t('crmImport.contactTable.selectPage')}
                  >
                    {allOnPageSelected
                      ? <CheckSquare className="w-5 h-5 text-[#4339A0]" />
                      : <Square className="w-5 h-5 text-gray-400" />}
                  </button>
                </th>
                {visibleColumns.map((column) => {
                  const isActive = sortKey === column.key;
                  return (
                    <th key={column.key} className="px-4 py-2 text-left">
                      <button
                        onClick={() => toggleSort(column.key)}
                        title={t('crmImport.contactTable.sortBy', { column: t(column.labelKey) })}
                        className={`h-10 -mx-2 px-2 inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold uppercase tracking-wide hover:bg-gray-200/70 active:scale-[0.96] transition-[background-color,color,transform] duration-150 ${
                          isActive ? 'text-[#4339A0]' : 'text-gray-600'
                        }`}
                      >
                        <span>{t(column.labelKey)}</span>
                        {/* Always-visible dim icon signals every column is sortable; the active
                            column swaps to a chevron that flips with the direction. */}
                        {isActive ? (
                          <ChevronUp
                            className={`w-3.5 h-3.5 transition-transform duration-200 ${
                              sortDirection === 'desc' ? 'rotate-180' : ''
                            }`}
                          />
                        ) : (
                          <ChevronsUpDown className="w-3.5 h-3.5 opacity-40" />
                        )}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageRows.map((contact) => {
                const blocker = blockerFor(contact);
                const isSelected = selectedIds.has(contact.id);
                return (
                  <tr
                    key={contact.id}
                    onClick={() => toggleContact(contact)}
                    className={`transition-colors duration-150 ${
                      blocker
                        ? 'bg-gray-50/60 cursor-not-allowed'
                        : isSelected
                          ? 'bg-[#f5f3ff] cursor-pointer'
                          : 'hover:bg-gray-50 cursor-pointer'
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="w-10 h-10 -m-2 grid place-items-center">
                        {blocker
                          ? <Square className="w-5 h-5 text-gray-300" />
                          : isSelected
                            ? <CheckSquare className="w-5 h-5 text-[#4339A0]" />
                            : <Square className="w-5 h-5 text-gray-400" />}
                      </div>
                    </td>
                    {visibleColumns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-4 py-3 text-sm ${blocker ? 'text-gray-400' : 'text-gray-700'}`}
                      >
                        {column.key === 'name' ? (
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${blocker ? 'text-gray-500' : 'text-gray-900'}`}>
                              {column.value(contact) || '—'}
                            </span>
                            {blocker && (
                              <span className="px-2 py-0.5 rounded-md text-[11px] font-medium bg-gray-200 text-gray-600 whitespace-nowrap">
                                {t(`crmImport.contactTable.blocker.${blocker === 'no-email' ? 'noEmail' : 'alreadyImported'}`)}
                              </span>
                            )}
                          </div>
                        ) : (
                          column.value(contact) || '—'
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-4 py-16 text-center">
                    <Search className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-pretty">{t('crmImport.contactTable.noResults')}</p>
                    {hasActiveFilters && (
                      <button
                        onClick={resetFilters}
                        className="mt-3 h-10 px-4 rounded-lg text-sm text-[#4339A0] hover:bg-[#f5f3ff] active:scale-[0.96] transition-[background-color,transform] duration-150"
                      >
                        {t('crmImport.contactTable.resetFilters')}
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 shadow-[inset_0_1px_0_rgba(0,0,0,0.08)]">
            <span className="text-sm text-gray-600 tabular-nums">
              {t('crmImport.contactTable.pageOf', { page: safePage, total: totalPages })}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
                className="w-10 h-10 grid place-items-center rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent active:scale-[0.96] transition-[background-color,transform] duration-150"
                aria-label={t('crmImport.contactTable.previousPage')}
              >
                <ChevronLeft className="w-4 h-4 text-gray-700" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                .map((n, index, visible) => (
                  <React.Fragment key={n}>
                    {index > 0 && n - visible[index - 1] > 1 && <span className="px-1 text-gray-400">…</span>}
                    <button
                      onClick={() => setPage(n)}
                      className={`min-w-10 h-10 px-2 rounded-lg text-sm tabular-nums active:scale-[0.96] transition-[background-color,color,transform] duration-150 ${
                        n === safePage
                          ? 'bg-[#4339A0] text-white font-semibold'
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {n}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages}
                className="w-10 h-10 grid place-items-center rounded-lg hover:bg-gray-200 disabled:opacity-30 disabled:hover:bg-transparent active:scale-[0.96] transition-[background-color,transform] duration-150"
                aria-label={t('crmImport.contactTable.nextPage')}
              >
                <ChevronRight className="w-4 h-4 text-gray-700" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactSelectionTable;
